import React, { useState } from "react";
import { logAction } from "../utils/logsApi";

const StrategyActivation = () => {
    const [username, setUsername] = useState("");
    const [licenseKey, setLicenseKey] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState(""); // "success", "error", "warning"

    const CLOUDFLARE_API_LICENSE_URL = "https://adamo-licenses.ream-850.workers.dev";
    const CLOUDFLARE_AUTH_TOKEN = "Bearer b7f3a1d81c2347ab9b52cd19d8e14c87df6f7c4f2ea74bc394c8f3027e38997b";
    const TRADINGVIEW_WORKER_URL = "https://tradingview-proxy.ream-850.workers.dev/";
    
    const MACD_SNIPER_STRATEGY = {
        id: "macd-sniper",
        name: "MACD Sniper",
        pine_id: "PUB;1167db8fd8f3406da2526efa1913d54d"
    };

    const SESSION_ID = "wqaha9g53kl7jgqifynhzw2w24c269mh";
    const SESSION_ID_SIGN = "v3:H/WXSTs2lUusduToZfQe1PL17suxt6eJKv1pBz+s0Q0=";

    const validateLicense = async (licenseKey) => {
        try {
            const response = await fetch(`${CLOUDFLARE_API_LICENSE_URL}/license/activate-strategy`, {
                method: "POST",
                headers: {
                    "Authorization": CLOUDFLARE_AUTH_TOKEN,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    licenseKey,
                    pine_id: MACD_SNIPER_STRATEGY.pine_id,
                    strategyName: MACD_SNIPER_STRATEGY.name
                })
            });

            const result = await response.json();
            const message = result.message || "Unknown error occurred.";

            if (result.success) {
                return { status: "success", data: result.data };
            } else if (message.toLowerCase().includes("expired")) {
                return { status: "expired", message: "License has expired." };
            } else if (message.toLowerCase().includes("suspended")) {
                return { status: "suspended", message: "License is suspended." };
            } else if (message.toLowerCase().includes("already used")) {
                return { status: "used", message: "License already used for this strategy." };
            } else if (message.toLowerCase().includes("invalid")) {
                return { status: "invalid", message: "Invalid license key." };
            } else {
                return { status: "error", message };
            }
        } catch (error) {
            console.error("🚨 License activation error:", error);
            return { status: "error", message: "Failed to activate license. Please try again." };
        }
    };

    const grantTradingViewAccess = async (username) => {
        try {
            
            const formData = new FormData();
            formData.append('pine_id', MACD_SNIPER_STRATEGY.pine_id);
            formData.append('username_recip', username.trim());
            formData.append('sessionid', SESSION_ID);
            formData.append('sessionid_sign', SESSION_ID_SIGN);
            formData.append('action', 'add');


            const response = await fetch(TRADINGVIEW_WORKER_URL, {
                method: "POST",
                body: formData
            });


            if (response.status === 201) {
                return { status: "success", message: "Access granted successfully" };
            } else if (response.status === 200) {
                const responseData = await response.json();
                
                if (responseData.status === "exists") {
                    return { status: "warning", message: "User already has access to this strategy." };
                } else {
                    return { status: "success", message: "Access granted successfully" };
                }
            } else if (response.status === 422) {
                const responseData = await response.json();
                
                if (responseData.code === "username_recip_not_found") {
                    return { status: "error", message: responseData.detail };
                } else {
                    return { status: "error", message: "Failed to grant access. Please try again." };
                }
            } else {
                return { status: "error", message: "Failed to grant access. Please try again." };
            }
        } catch (error) {
            console.error("🚨 TradingView access error:", error);
            return { status: "error", message: "An unexpected error occurred. Please try again." };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setMessageType("");

        if (!username.trim() || !licenseKey.trim()) {
            setMessage("Please fill in all fields.");
            setMessageType("error");
            logAction(
                "CLIENT_ACTIVATION_ATTEMPT",
                "PUBLIC_USER",
                `Client attempted activation with missing fields - Username: ${username || 'empty'}, License: ${licenseKey || 'empty'}`,
                licenseKey,
                "Strategy Activation"
            );
            return;
        }

        // Log the activation attempt
        logAction(
            "CLIENT_ACTIVATION_ATTEMPT",
            "PUBLIC_USER",
            `Client attempting to activate MACD Sniper strategy - Username: ${username}, License: ${licenseKey}`,
            licenseKey,
            "Strategy Activation"
        );

        setIsLoading(true);

        try {
            // Step 1: Validate license
            const licenseResult = await validateLicense(licenseKey);
            
            if (licenseResult.status !== "success") {
                setMessage(licenseResult.message);
                setMessageType("error");
                logAction(
                    "CLIENT_ACTIVATION_FAILED",
                    "PUBLIC_USER",
                    `Client activation failed - Username: ${username}, License: ${licenseKey}, Reason: ${licenseResult.status} - ${licenseResult.message}`,
                    licenseKey,
                    "Strategy Activation"
                );
                setIsLoading(false);
                return;
            }

            // Step 2: Grant TradingView access
            const accessResult = await grantTradingViewAccess(username);
            
            if (accessResult.status === "success") {
                setMessage("✅ Success! You now have access to the MACD Sniper strategy on TradingView.");
                setMessageType("success");
                logAction(
                    "CLIENT_ACTIVATION_SUCCESS",
                    "PUBLIC_USER",
                    `Client successfully activated MACD Sniper strategy - Username: ${username}, License: ${licenseKey}`,
                    licenseKey,
                    "Strategy Activation"
                );
                setUsername("");
                setLicenseKey("");
            } else if (accessResult.status === "warning") {
                setMessage(`⚠️ ${accessResult.message}`);
                setMessageType("warning");
                logAction(
                    "CLIENT_ACTIVATION_WARNING",
                    "PUBLIC_USER",
                    `Client activation warning - Username: ${username}, License: ${licenseKey}, Warning: ${accessResult.message}`,
                    licenseKey,
                    "Strategy Activation"
                );
            } else {
                setMessage(`❌ ${accessResult.message}`);
                setMessageType("error");
                logAction(
                    "CLIENT_ACTIVATION_FAILED",
                    "PUBLIC_USER",
                    `Client TradingView access failed - Username: ${username}, License: ${licenseKey}, Error: ${accessResult.message}`,
                    licenseKey,
                    "Strategy Activation"
                );
            }
        } catch (error) {
            console.error("Activation error:", error);
            setMessage("An unexpected error occurred. Please try again.");
            setMessageType("error");
            logAction(
                "CLIENT_ACTIVATION_ERROR",
                "PUBLIC_USER",
                `Client activation unexpected error - Username: ${username}, License: ${licenseKey}, Error: ${error.message}`,
                licenseKey,
                "Strategy Activation"
            );
        }

        setIsLoading(false);
    };

    const getMessageStyle = () => {
        const baseStyle = {
            padding: '15px',
            margin: '20px 0',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
        };

        switch (messageType) {
            case "success":
                return {
                    ...baseStyle,
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    border: '1px solid #c3e6cb'
                };
            case "warning":
                return {
                    ...baseStyle,
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    border: '1px solid #ffeeba'
                };
            case "error":
                return {
                    ...baseStyle,
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb'
                };
            default:
                return baseStyle;
        }
    };

    return (
        <div className="strategy-activation-container" style={{
            maxWidth: '500px',
            margin: '50px auto',
            padding: '30px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            fontFamily: 'Arial, sans-serif'
        }}>
            <h2 style={{
                textAlign: 'center',
                color: '#333',
                marginBottom: '30px',
                fontSize: '24px'
            }}>
                MACD Sniper Strategy Activation
            </h2>

            {message && (
                <div style={getMessageStyle()}>
                    {message}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        TradingView Username
                    </label>
                    <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter your TradingView username"
                        style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px',
                            boxSizing: 'border-box'
                        }}
                        disabled={isLoading}
                    />
                </div>

                <div style={{ marginBottom: '30px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        License Key
                    </label>
                    <input
                        type="text"
                        value={licenseKey}
                        onChange={(e) => setLicenseKey(e.target.value)}
                        placeholder="Enter your license key"
                        style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px',
                            boxSizing: 'border-box'
                        }}
                        disabled={isLoading}
                    />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        type="submit"
                        disabled={isLoading || !username.trim() || !licenseKey.trim()}
                        style={{
                            flex: 1,
                            padding: '12px',
                            backgroundColor: isLoading ? '#ccc' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: (isLoading || !username.trim() || !licenseKey.trim()) ? 0.6 : 1
                        }}
                    >
                        {isLoading ? "Processing..." : "Activate Strategy"}
                    </button>
                </div>
            </form>

            <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666',
                textAlign: 'center'
            }}>
                <p style={{ margin: '0 0 10px 0' }}>
                    <strong>What you'll get:</strong>
                </p>
                <ul style={{ margin: '0', paddingLeft: '20px', textAlign: 'left' }}>
                    <li>Access to MACD Sniper strategy on TradingView</li>
                </ul>
            </div>
        </div>
    );
};

export default StrategyActivation; 